<template><div><code-example source-code='&lt;dt-text kind=&quot;headline&quot; :size=&quot;600&quot;&gt;
  &lt;dt-motion-text
    text=&quot;Welcome to Dialtone Motion Text&quot;
    animation-mode=&quot;shimmer&quot;
    :auto-start=&quot;true&quot;
    loop
  /&gt;
&lt;/dt-text&gt;'>
<dt-text kind="headline" :size="600">
  <dt-motion-text
    text="Welcome to Dialtone Motion Text"
    animation-mode="shimmer"
    :auto-start="true"
    loop
  />
</dt-text>
</code-example>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The Motion Text component provides beautiful text animations with zero configuration required. Simply pass text and let it animate automatically.</p>
<h3 id="animation-modes" tabindex="-1" class="d-docsite--header-3">Animation Modes <a class="header-anchor d-link d-docsite--link d-link" href="#animation-modes" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The component supports six different animation modes:</p>
<code-example source-code='&lt;dt-text kind=&quot;headline&quot; :size=&quot;600&quot;&gt;
  &lt;dt-motion-text
    text=&quot;Welcome to Dialtone Motion Text&quot;
    :animation-mode=&quot;{mode}&quot;
  /&gt;
&lt;/dt-text&gt;'>
<dt-stack gap="200" align="center" class="d-hmn84">
  <dt-stack direction="row" gap="100">
    <dt-button
      v-for="mode in animationModes"
      :key="mode"
      :size="100"
      kind="muted"
      importance="outlined"
      @click="playMode(mode)"
    >
      {{ mode }}
    </dt-button>
  </dt-stack>
  <dt-text kind="headline" :size="600">
    <dt-motion-text
      ref="animDemoRef"
      text="Welcome to Dialtone Motion Text"
      :animation-mode="activeMode"
      :speed="300"
    />
  </dt-text>
</dt-stack>
</code-example>
<h3 id="speed-control" tabindex="-1" class="d-docsite--header-3">Speed Control <a class="header-anchor d-link d-docsite--link d-link" href="#speed-control" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-text kind=&quot;headline&quot; :size=&quot;600&quot;&gt;
  &lt;dt-motion-text
    text=&quot;Welcome to Dialtone Motion Text&quot;
    animation-mode=&quot;shimmer&quot;
    :speed=&quot;{speed}&quot;
    :auto-start=&quot;true&quot;
    loop
  /&gt;
&lt;/dt-text&gt;'>
<dt-stack gap="200">
  <dt-segmented-control :size="100" v-model="selected" aria-label="Speed Control">
    <dt-segmented-control-item  v-dt-tooltip="'Near-instant'" value="100" :selected="selected === '100'">100</dt-segmented-control-item>
    <dt-segmented-control-item  v-dt-tooltip="'Fast'" value="200" :selected="selected === '200'">200</dt-segmented-control-item>
    <dt-segmented-control-item  v-dt-tooltip="'Medium (default)'" value="300" :selected="selected === '300'">300</dt-segmented-control-item>
    <dt-segmented-control-item  v-dt-tooltip="'Slow'" value="400" :selected="selected === '400'">400</dt-segmented-control-item>
    <dt-segmented-control-item  v-dt-tooltip="'Very slow'" value="500" :selected="selected === '500'">500</dt-segmented-control-item>
  </dt-segmented-control>
  <dt-text kind="headline" :size="600">
    <dt-motion-text
      text="Welcome to Dialtone Motion Text"
      animation-mode="shimmer"
      :speed="Number(selected)"
      :auto-start="true"
      loop
    />
  </dt-text>
</dt-stack>
</code-example>
<h3 id="manual-control" tabindex="-1" class="d-docsite--header-3">Manual Control <a class="header-anchor d-link d-docsite--link d-link" href="#manual-control" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Take full control of the animation lifecycle:</p>
<code-example source-code='&lt;dt-button @click=&quot;$refs.textRef.start()&quot;&gt;Start&lt;/dt-button&gt;
&lt;dt-button @click=&quot;$refs.textRef.pause()&quot;&gt;Pause&lt;/dt-button&gt;
&lt;dt-button @click=&quot;$refs.textRef.resume()&quot;&gt;Resume&lt;/dt-button&gt;
&lt;dt-button @click=&quot;$refs.textRef.reset()&quot;&gt;Reset&lt;/dt-button&gt;
&lt;dt-button @click=&quot;$refs.textRef.skipToEnd()&quot;&gt;Skip to End&lt;/dt-button&gt;
&lt;dt-motion-text
  ref=&quot;textRef&quot;
  text=&quot;Welcome to Dialtone Motion Text&quot;
  animation-mode=&quot;shimmer&quot;
  :auto-start=&quot;false&quot;
/&gt;'>
<dt-stack gap="200" align="center">
  <dt-stack direction="row" gap="100">
    <dt-button :size="100" kind="muted" importance="outlined" @click="manualDemoRef.start()">Start</dt-button>
    <dt-button :size="100" kind="muted" importance="outlined" @click="manualDemoRef.pause()">Pause</dt-button>
    <dt-button :size="100" kind="muted" importance="outlined" @click="manualDemoRef.resume()">Resume</dt-button>
    <dt-button :size="100" kind="muted" importance="outlined" @click="manualDemoRef.reset()">Reset</dt-button>
    <dt-button :size="100" kind="muted" importance="outlined" @click="manualDemoRef.skipToEnd()">Skip to End</dt-button>
  </dt-stack>
  <dt-text kind="headline" :size="600">
    <dt-motion-text
      ref="manualDemoRef"
      text="Welcome to Dialtone Motion Text"
      animation-mode="shimmer"
      :auto-start="true"
      loop
    />
  </dt-text>
</dt-stack>
</code-example>
<h3 id="looping-animation" tabindex="-1" class="d-docsite--header-3">Looping Animation <a class="header-anchor d-link d-docsite--link d-link" href="#looping-animation" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Perfect for attention-grabbing headers or hero sections:</p>
<code-example only-show="code" source-code='&lt;dt-motion-text
  text=&quot;Continuous animation&quot;
  animation-mode=&quot;slide-in&quot;
  :loop=&quot;true&quot;
  :speed=&quot;200&quot;
/&gt;'>
<dt-motion-text
  text="Continuous animation"
  animation-mode="slide-in"
  :loop="true"
  :speed="200"
/>
</code-example>
<h3 id="using-slots" tabindex="-1" class="d-docsite--header-3">Using Slots <a class="header-anchor d-link d-docsite--link d-link" href="#using-slots" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">You can also use the default slot instead of the text prop:</p>
<code-example only-show="code" source-code='&lt;dt-motion-text animation-mode=&quot;fade-in&quot;&gt;
  &lt;span&gt;Animated &lt;/span&gt;
  &lt;strong&gt;text&lt;/strong&gt;
&lt;/dt-motion-text&gt;'>
<dt-motion-text animation-mode="fade-in">
  <span>Animated </span>
  <strong>text</strong>
</dt-motion-text>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="motiontext" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The Motion Text component is built with accessibility as a core principle.</p>
<h3 id="reduced-motion-support" tabindex="-1" class="d-docsite--header-3">Reduced Motion Support <a class="header-anchor d-link d-docsite--link d-link" href="#reduced-motion-support" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Automatically respects the user's <code v-pre>prefers-reduced-motion</code> system setting. When enabled, animations are skipped and text appears instantly.</p>
<div class="language-vue" data-highlighter="prismjs" data-ext="vue"><pre v-pre><code class="language-vue"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>dt-motion-text</span></span>
<span class="line">  <span class="token attr-name">text</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>Respects user preferences<span class="token punctuation">"</span></span></span>
<span class="line">  <span class="token attr-name">:respects-reduced-motion</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>true<span class="token punctuation">"</span></span></span>
<span class="line"><span class="token punctuation">/></span></span></span>
<span class="line"></span></code></pre>
</div><h3 id="screen-reader-support" tabindex="-1" class="d-docsite--header-3">Screen Reader Support <a class="header-anchor d-link d-docsite--link d-link" href="#screen-reader-support" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Provide alternative text for screen readers:</p>
<div class="language-vue" data-highlighter="prismjs" data-ext="vue"><pre v-pre><code class="language-vue"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>dt-motion-text</span></span>
<span class="line">  <span class="token attr-name">text</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>🎉 Congratulations!<span class="token punctuation">"</span></span></span>
<span class="line">  <span class="token attr-name">screen-reader-text</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>Congratulations<span class="token punctuation">"</span></span></span>
<span class="line"><span class="token punctuation">/></span></span></span>
<span class="line"></span></code></pre>
</div><h3 id="aria-attributes" tabindex="-1" class="d-docsite--header-3">ARIA Attributes <a class="header-anchor d-link d-docsite--link d-link" href="#aria-attributes" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The component automatically includes proper ARIA attributes:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><code v-pre>aria-live=&quot;polite&quot;</code> during animation</li>
<li class="d-docsite--list-element"><code v-pre>aria-label</code> when screen reader text is provided</li>
<li class="d-docsite--list-element"><code v-pre>aria-hidden</code> for animated content while animating</li>
</ul>
<h2 id="best-practices" tabindex="-1" class="d-docsite--header-2">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h2>
<ol class="d-docsite--ordered-list">
<li class="d-docsite--list-element"><strong>Choose the right speed</strong>: Use <code v-pre>200</code> for short text, <code v-pre>400</code> for longer passages</li>
<li class="d-docsite--list-element"><strong>Don't overuse</strong>: Too many animated elements can be distracting</li>
<li class="d-docsite--list-element"><strong>Consider context</strong>: Hero sections work well with <code v-pre>gradient-in</code>, while body text might be better with <code v-pre>fade-in</code></li>
<li class="d-docsite--list-element"><strong>Test with reduced motion</strong>: Always ensure your UI works with animations disabled</li>
<li class="d-docsite--list-element"><strong>Provide screen reader text</strong>: If using emojis or special characters, always include alternative text</li>
</ol>
</div></template>


<script setup>
import { ref, nextTick } from 'vue';

const animDemoRef = ref(null);
const manualDemoRef = ref(null);
const activeMode = ref('none');
const selected = ref('300');

const animationModes = ['gradient-in', 'fade-in', 'slide-in', 'gradient-sweep', 'shimmer'];

async function playMode (mode) {
  // Force re-render even when clicking the same mode twice
  activeMode.value = '';
  await nextTick();
  activeMode.value = mode;
  await nextTick();
  if (!animDemoRef.value) return;
  animDemoRef.value.reset();
  animDemoRef.value.start();
}
</script>